import { MicDeviceType } from "@/common/enums/enums";
import { useCallback, useEffect, useState } from "react";

export interface MicDevice {
  label: string;
  value: string;
  default: boolean;
  type: MicDeviceType;
}

export const useAudioInputDevices = (): {
  mics: MicDevice[];
  loading: boolean;
  refresh: () => Promise<void>;
} => {
  const [mics, setMics] = useState<MicDevice[]>([]);
  const [loading, setLoading] = useState(false);

  async function ensurePermission(): Promise<void> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => {
        track.stop();
      });
    } catch {
      // soft catch
    }
  }

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      if (!navigator?.mediaDevices?.enumerateDevices) {
        setMics([]);
        return;
      }
      await ensurePermission();
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter((d) => d.kind === "audioinput");
      const options: MicDevice[] = audioInputs.map((d, index) => {
        return {
          default: index === 0,
          value: d.deviceId,
          label: d.label?.trim() || `Microphone ${index + 1}`,
          type: MicDeviceType.DESKTOP,
        };
      });
      setMics(options);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const onChange = async (): Promise<void> => {
      await refresh();
    };
    navigator.mediaDevices?.addEventListener?.("devicechange", () => {
      void onChange();
    });
    return (): void => {
      navigator.mediaDevices?.removeEventListener?.("devicechange", () => {
        void onChange();
      });
    };
  }, [refresh]);

  return { mics, loading, refresh };
};
